import { BALANCE } from '../balance.ts';
import { getMap, roomDef } from '../maps/graph.ts';
import type { GameResult, GameState, PlayerId, Plan, Shares } from '../types.ts';

/** Base loot plus the extras of the alcoves a plan asks for (the infiltrator's yardstick). */
export function plannedLootOf(s: GameState, plan: Plan | null): number {
  const map = getMap(s.config.mapId);
  const extras = (plan?.detours ?? []).reduce((sum, d) => sum + roomDef(map, d.room).extraLoot, 0);
  return map.baseLoot + extras;
}

/** Percentage of the loot lost to the alarm: 10 % per full 10 points above 50 % (GDD §7.2). */
export function lootPenaltyPercent(alarmFinalTenths: number): number {
  const over = alarmFinalTenths - BALANCE.lootPenaltyStartTenths;
  if (over <= 0) return 0;
  return Math.floor(over / BALANCE.lootPenaltyStepTenths) * BALANCE.lootPenaltyPercentPerStep;
}

/** Splits `loot` by percentage; each share is rounded down (GDD §5.6). */
export function sharesToAmounts(loot: number, pct: Shares): Shares {
  const out: Shares = {};
  for (const [id, p] of Object.entries(pct)) out[id] = Math.floor((loot * p) / 100);
  return out;
}

export function equalShares(ids: readonly PlayerId[]): Shares {
  const out: Shares = {};
  if (ids.length === 0) return out;
  const base = Math.floor(100 / ids.length);
  ids.forEach((id, i) => {
    out[id] = i === 0 ? 100 - base * (ids.length - 1) : base;
  });
  return out;
}

/** GDD §7.1–§7.3, applied to the finished game. */
export function computeResult(
  s: GameState,
  success: boolean,
  failure: GameResult['failure'],
): GameResult {
  const inf = s.infiltrator.id;
  const exec = s.exec;
  const plan = exec?.plan ?? s.planning.approved;
  const planned = plannedLootOf(s, plan);
  const alarmFinal = exec?.alarmFinal ?? s.alarm;
  const infiltratorScore = Math.floor((planned * BALANCE.infiltratorScorePercent) / 100);

  if (!success) {
    return {
      success: false,
      failure,
      infiltrator: inf,
      expelled: null,
      winners: inf ? [inf] : [],
      loot: 0,
      lootBase: 0,
      lootExtras: 0,
      penaltyPercent: 0,
      plannedLoot: planned,
      alarmFinal,
      shares: {},
      scores: inf ? { [inf]: infiltratorScore } : {},
    };
  }

  const map = getMap(s.config.mapId);
  const extras = (exec?.completedOptional ?? []).reduce(
    (sum, room) => sum + roomDef(map, room).extraLoot,
    0,
  );
  const penalty = lootPenaltyPercent(alarmFinal);
  const loot = Math.floor(((map.baseLoot + extras) * (100 - penalty)) / 100);
  const expelled = s.hideout?.expelled ?? null;
  const pct = s.hideout?.finalShares ?? {};
  const shares = sharesToAmounts(loot, pct);

  let winners: PlayerId[];
  if (inf) {
    winners = expelled === inf ? s.players.filter((p) => p.id !== inf).map((p) => p.id) : [inf];
  } else {
    winners = s.players.filter((p) => p.id !== expelled).map((p) => p.id);
  }
  const scores: Record<PlayerId, number> = {};
  for (const id of winners) scores[id] = id === inf ? infiltratorScore : (shares[id] ?? 0);

  return {
    success: true,
    failure: null,
    infiltrator: inf,
    expelled,
    winners,
    loot,
    lootBase: map.baseLoot,
    lootExtras: extras,
    penaltyPercent: penalty,
    plannedLoot: planned,
    alarmFinal,
    shares,
    scores,
  };
}
